import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Folder,
  MessageSquare,
  Plus,
  Search,
  Send,
  Menu,
  Bot,
  Loader2,
  AlertTriangle,
  ChevronDown,
  MoreHorizontal,
} from 'lucide-react';
import { auth } from "../firebase.js";
import DanteLogo from "../assets/Dante-White-Full-01.png";
import DanteIcon from "../assets/Dante-White-Icon-01.png";

// --- CONFIGURATION ---
const API_BASE_URL = "https://dante-rag-api-84264019333.europe-west1.run.app";

async function getFirebaseAuthToken() {
  const user = auth.currentUser;
  
  if (user) {
    try {
      // This is the key Firebase function.
      // It gets the token AND automatically refreshes it if it's expired.
      const idToken = await user.getIdToken();
      return idToken;
    } catch (error) {
      console.error("Error getting auth token:", error);
      throw new Error("Your session has expired. Please sign in again.");
    }
  } else {
    // This will happen if the page loads before Firebase has signed in the user
    // or if the user isn't logged in at all.
    console.warn("No user is currently signed in.");
    throw new Error("No user signed in. Please log in to continue.");
  }
}

// --- Time-of-day greeting (MOVED OUTSIDE) ---
const getTimeOfDayGreeting = () => {
  const now = new Date();
  const day = now.getDay(); // 0 for Sunday, 1 for Monday, ..., 5 for Friday, 6 for Saturday
  const hour = now.getHours();

  if (day === 5 && hour >= 6 && hour < 12) { // Friday morning (e.g., 6 AM to 11:59 AM)
    return "Got that Friday feeling!";
  }

  if (day === 1 && hour >= 9 && hour < 12) { // Monday morning (9 AM to 11:59 AM)
    return "The best things start here! Let's get to work.";
  }

  if (hour >= 2 && hour < 4) return "Working into the spooky hours, lets get to it";
  if (hour < 12) return "Good morning! Ready for analysis.";
  if (hour < 18) return "Good afternoon! What's on your mind?";
  return "Good evening! Let's review your data.";
};

// --- RENDERING COMPONENTS (MOVED OUTSIDE & MEMOIZED) ---

const HeroScreen = React.memo(() => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <img 
          src={DanteLogo} 
          alt="Dante AI" 
          className="w-56 h-auto object-contain mb-12 opacity-90 animate-fadedown" 
          style={{ animationDelay: '0s' }}
      />
      <h1 
          className="text-3xl font-grotesk font-bold text-white mb-3 animate-fadedown"
          style={{ animationDelay: '0.1s' }}
      >
          {getTimeOfDayGreeting()}
      </h1>
  </div>
));
HeroScreen.displayName = "HeroScreen"; // For easier debugging

const ThinkingIndicator = React.memo(() => (
  <div className="flex justify-start items-center space-x-3 animate-fade-in-up-subtle">
    <img 
      src={DanteIcon} 
      alt="Thinking..." 
      className="w-5 h-5 shrink-0 object-contain opacity-80" 
    />
    <div className="flex items-center space-x-1.5">
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse-dot-1"></div>
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse-dot-2"></div>
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse-dot-3"></div>
    </div>
  </div>
));
ThinkingIndicator.displayName = "ThinkingIndicator";

const MessagesView = React.memo(({ chat, isLoading, selectedChatId, messagesEndRef }) => (
  <div className="space-y-6">
    {chat?.messages?.map((msg, i) => (
      <div
        key={`${chat.id}-${i}-${msg.timestamp}`}
        className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} animate-fade-in-up-subtle`}
      >
        {msg.sender === "user" ? (
          <div className="max-w-2xl px-4 py-3 rounded-2xl bg-indigo-600 text-white whitespace-pre-wrap text-base shadow-md font-sans leading-relaxed">
            {msg.text}
          </div>
        ) : msg.sender === 'system' ? (
          <div className="flex items-start max-w-2xl px-4 py-3 rounded-2xl bg-red-800 text-red-100 border border-red-700 whitespace-pre-wrap text-base shadow-md">
             <AlertTriangle size={18} className="shrink-0 mr-2.5 align-text-bottom text-red-300" />
             <span className="leading-relaxed font-sans">{msg.text}</span>
          </div>
        ) : (
          <div className="flex items-start max-w-2xl space-x-3">
            <img 
              src={DanteIcon} 
              alt="Dante AI" 
              className="w-5 h-5 shrink-0 object-contain mt-0.5 opacity-90" 
            />
            <span className="text-slate-100 whitespace-pre-wrap text-base leading-relaxed font-sans">
              {msg.text}
            </span>
          </div>
        )}
      </div>
    ))}
    {isLoading && selectedChatId === chat?.id && (
        <ThinkingIndicator />
    )}
    <div ref={messagesEndRef} />
  </div>
));
MessagesView.displayName = "MessagesView";


// --- MAIN COMPONENT ---
function DanteChatBot({ campaignId }) {
  // Sidebar defaults to collapsed (false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Original State
  const [folders, setFolders] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatStarted, setChatStarted] = useState(false);
  const [inputText, setInputText] = useState("");
  const messagesEndRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sidebar State
  const [searchTerm, setSearchTerm] = useState("");
  const [openFolders, setOpenFolders] = useState({});

  // Chat Action Menu State
  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const chatMenuRef = useRef(null);

  // --- MODAL STATE (NEW) ---
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [chatToModify, setChatToModify] = useState(null); // Holds the chat object
  const [newChatTitle, setNewChatTitle] = useState(""); // For rename input

  // Key-Tap Pulse State
  const [isTyping, setIsTyping] = useState(false);
  const typingTimerRef = useRef(null);
  const user = auth.currentUser;

  // --- API CALL: Fetch Initial Chats & Folders ---
  const fetchInitialData = useCallback(async () => {
    setIsInitialLoading(true);
    setError(null);
    setSelectedChatId(null);
    setChatStarted(false);
    try {
      const token = await getFirebaseAuthToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      const response = await fetch(`${API_BASE_URL}/get-chats-folders`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (jsonError)
{
            errorData = { detail: response.statusText || `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const loadedFolders = data.folders || [];
      setFolders(loadedFolders);
      setChats(data.chats || []);

      const allOpen = loadedFolders.reduce((acc, f) => {
        acc[f.id] = true;
        return acc;
      }, {});
      setOpenFolders(allOpen);

    } catch (err) {
      console.error("Failed to fetch initial data:", err);
      setError(`Failed to load initial data: ${err.message}.`);
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Scroll to bottom
  useEffect(() => {
    if (selectedChatId) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedChatId, chats]);

  // Close chat menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      // Close Chat Menu
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setIsChatMenuOpen(false);
      }
      // Close Modals on outside click (if needed, but clicking "Cancel" is cleaner)
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [chatMenuRef]);


  // Select chat
  function selectChat(id) {
    setSelectedChatId(id);
    setChatStarted(true);
    setError(null);
  }

  // Start new chat
  function startNewChat() {
    const newChatId = `chat-${Date.now()}`;
    const newChat = {
        id: newChatId,
        title: "New Dante Query",
        folderId: null,
        messages: [],
    };
    setChats(prev => [newChat, ...prev]);
    setSelectedChatId(newChatId);
    setChatStarted(true);
    setError(null);
    if (!isSidebarOpen) {
        setIsSidebarOpen(true);
    }
  }

  // --- API CALL: Send Message (Unchanged logic) ---
  async function sendMessage() {
    const currentInput = inputText.trim();
    if (!currentInput) return;

    setIsLoading(true);
    setError(null);
    setInputText("");

    let targetChatId = selectedChatId;
    let isNewChat = false;

    if (!targetChatId) {
        isNewChat = true;
        targetChatId = `chat-${Date.now()}`;
        const newChat = {
            id: targetChatId,
            title: currentInput.substring(0, 30) + (currentInput.length > 30 ? '...' : ''),
            folderId: null,
            messages: [],
        };
        setChats(prev => [newChat, ...prev]);
        setSelectedChatId(targetChatId);
        setChatStarted(true);
    }

    const userMsg = {
      role: "user",
      content: currentInput,
      sender: "user",
      text: currentInput,
      timestamp: Date.now(),
    };

     setChats((prev) =>
      prev.map((chat) =>
        chat.id === targetChatId
          ? { ...chat, messages: [...(chat.messages || []), userMsg] }
          : chat
      )
    );

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    const chatForHistory = chats.find(c => c.id === targetChatId);
    const historyForAPI = isNewChat ? [] : (chatForHistory?.messages || []);

    const requestBody = {
      campaign_id: campaignId,
      question: currentInput,
      session_id: targetChatId,
      chat_history: historyForAPI.map(({ role, content }) => ({ role, content })),
    };

    try {
      const token = await getFirebaseAuthToken();
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      const response = await fetch(`${API_BASE_URL}/query`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (jsonError) {
            errorData = { detail: response.statusText || `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setChats((prev) =>
        prev.map((chat) =>
          chat.id === data.session_id
            ? { ...chat, messages: data.new_chat_history.map(msg => ({
                  ...msg,
                  sender: msg.role === 'user' ? 'user' : 'bot',
                  text: msg.content,
                  timestamp: Date.now()
                }))
              }
            : chat
        )
      );
       if (data.session_id !== targetChatId) {
            setSelectedChatId(data.session_id);
       }

    } catch (err) {
      console.error("Failed to send message:", err);
      setError(`Failed to get response: ${err.message}.`);
      const errorMsg = {
          role: 'system', sender: 'system', text: `Error: ${err.message}`, timestamp: Date.now()
      };
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === targetChatId
            ? { ...chat, messages: [...(chat.messages || []), errorMsg] }
            : chat
        )
      );
    } finally {
      setIsLoading(false);
    }
  }


  // --- Filtered Chats & Folder Grouping (Unchanged) ---
  const filteredChats = chats.filter(chat =>
    chat.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const chatsByFolder = folders.reduce((acc, folder) => {
    acc[folder.id] = filteredChats.filter((c) => c.folderId === folder.id);
    return acc;
  }, {});

  const ungroupedChats = filteredChats.filter(c =>
    !c.folderId || !folders.find(f => f.id === c.folderId)
  );

  const selectedChat = chats.find((c) => c.id === selectedChatId);

  // --- Folder Toggle (Unchanged) ---
  const toggleFolder = (folderId) => {
    setOpenFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // --- Handle Key-Tap Pulse (Unchanged) ---
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isLoading) {
      sendMessage();
    } else if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
      // Pulse on typing
      setIsTyping(true);
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
      typingTimerRef.current = setTimeout(() => {
        setIsTyping(false);
        typingTimerRef.current = null;
      }, 150); // Pulse duration
    }
  };

  // --- MODAL ACTION FUNCTIONS (NEW) ---

  // Open Rename Modal
  const openRenameModal = (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;

    setChatToModify(chat);
    setNewChatTitle(chat.title); // Pre-fill with current title
    setIsRenameModalOpen(true);
    setIsChatMenuOpen(false); // Close the 'More' menu
  };

  // Open Delete Modal
  const openDeleteModal = (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;

    setChatToModify(chat);
    setIsDeleteModalOpen(true);
    setIsChatMenuOpen(false); // Close the 'More' menu
  };


  // --- CONFIRM RENAME ---
  const confirmRename = () => {
    if (!chatToModify || newChatTitle.trim() === "") {
      setIsRenameModalOpen(false);
      return;
    }

    setChats(prevChats => 
      prevChats.map(chat => 
        chat.id === chatToModify.id ? { ...chat, title: newChatTitle.trim() } : chat
      )
    );
    setIsRenameModalOpen(false);
    setChatToModify(null);
    setNewChatTitle("");
  };

  // --- CONFIRM DELETE ---
  const confirmDelete = () => {
    if (!chatToModify) {
      setIsDeleteModalOpen(false);
      return;
    }

    const chatIdToDelete = chatToModify.id;

    setChats(prevChats => prevChats.filter(chat => chat.id !== chatIdToDelete));

    if (selectedChatId === chatIdToDelete) {
      setSelectedChatId(null);
      setChatStarted(false);
      setError(null);
    }
    setIsDeleteModalOpen(false);
    setChatToModify(null);
  };


  // --- (DELETED: HeroScreen, ThinkingIndicator, MessagesView definitions were here) ---


  // Define shadow classes for input bar
  const baseShadow = 'drop-shadow-[0_0_3px_rgba(203,213,225,0.05)]';
  const focusShadow = 'focus-within:drop-shadow-[0_0_8px_rgba(203,213,225,0.4)]';
  const typingShadow = 'drop-shadow-[0_0_10px_rgba(203,213,225,0.6)]';


  // --- MAIN RETURN ---
  return (
    <div className="flex bg-slate-950 text-white h-full w-full">

      {/* Sidebar: bg-slate-900 (Unchanged) */}
       <aside className={`shrink-0 flex flex-col bg-slate-900 border-r border-slate-700 h-full transition-all duration-300 ease-in-out ${
         isSidebarOpen ? 'w-80' : 'w-0 overflow-hidden border-r-0'
       }`}>
        {/* (Sidebar content is unchanged) */}
        <div className="p-3 border-b border-slate-700">
            <button
                onClick={startNewChat}
                className="w-full flex items-center justify-center p-3 text-sm font-bold bg-accent-600 hover:bg-accent-500 text-white rounded-lg transition-colors"
            >
                <Plus size={16} className="mr-2" /> New Chat
            </button>
        </div>
        <div className="p-3 border-b border-slate-700">
            <div className="relative">
                <input
                type="text"
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-2 py-1.5 text-sm rounded-lg bg-slate-700 text-white placeholder-slate-400 border border-slate-600 focus:ring-1 focus:ring-accent-600 focus:border-accent-600"
                />
                <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {folders.map((folder) => (
            <div key={folder.id}>
                <button
                    className="flex items-center justify-between w-full text-left px-2 py-1.5 text-xs uppercase tracking-wider font-grotesk font-bold text-slate-400 hover:text-slate-200"
                    onClick={() => toggleFolder(folder.id)}
                >
                    <span className="flex items-center">
                        <Folder size={14} className="mr-2 text-accent-400" />
                        {folder.name}
                    </span>
                    <ChevronDown size={16} className={`transition-transform ${openFolders[folder.id] ? 'rotate-180' : 'rotate-0'}`} />
                </button>
                {openFolders[folder.id] && (
                    <div className="pl-2 pr-1 pt-1 space-y-1">
                    {chatsByFolder[folder.id]?.length ? (
                        chatsByFolder[folder.id].map((chat) => (
                        <button
                            key={chat.id}
                            className={`w-full text-left p-2 rounded-md transition-colors text-sm truncate font-sans ${
                            chat.id === selectedChatId
                                ? "bg-accent-600/30 font-semibold text-white"
                                : "hover:bg-slate-700/50 text-slate-300"
                            }`}
                            onClick={() => selectChat(chat.id)}
                            title={chat.title}
                        >
                            <MessageSquare size={14} className="inline mr-2 align-text-bottom opacity-80" />
                            {chat.title}
                        </button>
                        ))
                    ) : (
                        <div className="px-2 py-1 text-xs text-slate-500 italic">
                        No chats found
                        </div>
                    )}
                    </div>
                )}
            </div>
            ))}
            {ungroupedChats.length > 0 && (
                <div>
                    <div className="px-2 py-1.5 text-xs uppercase tracking-wider font-grotesk font-bold text-slate-400">
                        Chats
                    </div>
                     <div className="pl-2 pr-1 pt-1 space-y-1">
                        {ungroupedChats.map((chat) => (
                            <button
                                key={chat.id}
                                className={`w-full text-left p-2 rounded-md transition-colors text-sm truncate font-sans ${
                                chat.id === selectedChatId
                                    ? "bg-accent-600/30 font-semibold text-white"
                                    : "hover:bg-slate-700/50 text-slate-300"
                                }`}
                                onClick={() => selectChat(chat.id)}
                                title={chat.title}
                            >
                                <MessageSquare size={14} className="inline mr-2 align-text-bottom opacity-80" />
                                {chat.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        
        {/* --- Header (Fixed Top Left) --- */}
        <div className="absolute top-4 left-3 z-10 flex items-center gap-3">
           <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-md hover:bg-slate-800 transition-colors text-slate-400 hover:text-white pointer-events-auto"
              title={isSidebarOpen ? "Collapse Navigation" : "Expand Navigation"}
          >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <img src={DanteLogo} alt="Dante AI" className="h-7 w-auto opacity-90 object-contain pointer-events-auto" />
        </div>

        {/* --- Header (Fixed Top Right) --- */}
        {selectedChatId && (
            <div ref={chatMenuRef} className="absolute top-4 right-3 z-10 pointer-events-auto">
                <button
                    onClick={() => setIsChatMenuOpen(prev => !prev)}
                    className="p-2 rounded-md hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
                    title="Chat Actions"
                >
                    <MoreHorizontal size={24} />
                </button>
                {isChatMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 overflow-hidden">
                        <button 
                          onClick={() => openRenameModal(selectedChatId)}
                          className="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 transition-colors"
                        >
                          Rename Chat
                        </button>
                        <button 
                          onClick={() => openDeleteModal(selectedChatId)}
                          className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700 border-t border-slate-700 transition-colors"
                        >
                          Delete Chat
                        </button>
                    </div>
                )}
            </div>
        )}


        {/* --- CHAT SECTION --- */}
        {/* REVISION: Removed bottom padding (pb-24). The footer is now static, so we don't need to clear it manually. */}
        <section className="flex-1 flex flex-col overflow-y-auto bg-slate-950 pt-20">
             <div className="max-w-3xl w-full mx-auto px-4 md:px-6 flex-1 flex flex-col">
                 {isInitialLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                      <Loader2 size={32} className="animate-spin mr-3" /> Loading chat history...
                    </div>
                 ) : error && !selectedChatId ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-red-400 p-4">
                        <AlertTriangle size={32} className="mb-2" />
                        <p className="font-semibold font-grotesk">Error Loading Data</p>
                        <p className="text-sm text-center max-w-md">{error}</p>
                        <button
                            onClick={fetchInitialData}
                            className="mt-4 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded-lg"
                        >
                            Retry Load
                        </button>
                    </div>
                 ) : selectedChatId && chatStarted && selectedChat ? (
                      <MessagesView 
                        chat={selectedChat}
                        isLoading={isLoading}
                        selectedChatId={selectedChatId}
                        messagesEndRef={messagesEndRef} 
                      />
                 ) : (
                    <HeroScreen />
                 )}
             </div>
        </section>

        {/* --- Error Toast (Moved here so it floats above everything) --- */}
        {error && selectedChatId && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 p-2.5 bg-danger-500 text-white text-xs rounded-lg shadow-lg flex items-center animate-shake z-30">
                <AlertTriangle size={14} className="mr-2"/> {error}
            </div>
        )}


        {/* --- INPUT FOOTER --- */}
        {/* REVISION: Changed from 'absolute' to a standard flex item (shrink-0). This forces it to the bottom and pushes the chat area up. */}
        <footer className="shrink-0 w-full p-3 bg-slate-950 z-20">
            <div className={`relative max-w-3xl mx-auto bg-slate-800 rounded-xl border border-slate-700 
                            transition-all duration-200 pointer-events-auto 
                            ${isTyping ? typingShadow : `${baseShadow} ${focusShadow}`}`}
            >
                <input
                    type="text"
                    placeholder={selectedChatId ? "Type your query..." : "Start a new query here..."}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-5 pr-14 py-3 bg-transparent text-white placeholder-slate-400 focus:outline-none text-base disabled:opacity-50 font-sans"
                    disabled={isLoading || isInitialLoading}
                    autoFocus
                />
                <button
                    onClick={sendMessage}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:text-slate-600 shrink-0"
                    disabled={!inputText.trim() || isLoading || isInitialLoading}
                    title="Send Message"
                >
                {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                </button>
            </div>

            <p className="text-center text-xs text-slate-500 mt-2.5 px-3 pointer-events-auto max-w-3xl mx-auto">
              Dante can make mistakes. Please double-check important information.
            </p>
        </footer>

        {/* --- CUSTOM MODALS (NEW) --- */}
        
        {/* Rename Modal */}
        {isRenameModalOpen && chatToModify && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-800 rounded-xl p-6 w-96 shadow-[0_0_15px_rgba(255,255,255,0.4)] border border-slate-700 animate-fade-in-up-subtle">
              <h3 className="text-xl font-bold text-white mb-4">Rename Chat</h3>
              <p className="text-sm text-slate-300 mb-3">Enter a new name for the chat <strong>{chatToModify.title}</strong></p>
              <input
                type="text"
                value={newChatTitle}
                onChange={(e) => setNewChatTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && confirmRename()}
                className="w-full px-3 py-2 text-sm rounded-lg bg-slate-700 text-white placeholder-slate-400 border border-slate-600 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 mb-6"
                autoFocus
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsRenameModalOpen(false)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRename}
                  disabled={!newChatTitle.trim()}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-colors"
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {isDeleteModalOpen && chatToModify && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-800 rounded-xl p-6 w-96 shadow-[0_0_15px_rgba(255,255,255,0.4)] border border-slate-700 animate-fade-in-up-subtle">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center">
                <AlertTriangle size={20} className="mr-2"/> Confirm Deletion
              </h3>
              <p className="text-sm text-slate-300 mb-6">
                Are you sure you want to permanently delete the chat <strong>{chatToModify.title}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

export default DanteChatBot;